var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { Field as FormikField, getIn } from 'formik';
import { genId, isDifferent } from "../../../utils";
import { TextInput } from '../../TextInput';
import { FormGroup } from '../FormGroup';
// This is a user defined typeguard
function isChangeEvent(ev) {
    return ev && ev.target !== undefined;
}
function FormControl(_a) {
    var disabled = _a.disabled, isInline = _a.isInline, label = _a.label, name = _a.name, id = _a.id, render = _a.render, required = _a.required, fieldProps = _a.fieldProps, groupLss = _a.groupLss, labelLss = _a.labelLss, description = _a.description, _b = _a.rendersFormGroup, rendersFormGroup = _b === void 0 ? true : _b, showValidationToolTip = _a.showValidationToolTip, setIsDirty = _a.setIsDirty, isDirty = _a.isDirty;
    if (process.env.NODE_ENV !== 'production') {
        // This is a helper for simple values, for nested values the user is on their own
        if (name.indexOf('.') === -1 &&
            getIn(fieldProps.form.initialValues, name) === undefined) {
            throw new Error(name + " is not listed in the initialValues: " + JSON.stringify(fieldProps.form.initialValues));
        }
    }
    var error = getIn(fieldProps.form.errors, name);
    var isTouched = getIn(fieldProps.form.touched, name);
    var isInvalid = (!!error && !!isTouched) || false;
    var descriptionId = (error && isTouched) || !!description ? id + "-description" : undefined;
    // allows touched state to return to false if value matches initial value again
    var toggleFieldTouched = function (initialValue, newValue) {
        var hasDifference = isDifferent(initialValue, newValue);
        fieldProps.form.setFieldTouched(name, hasDifference);
        setIsDirty(hasDifference);
    };
    var content = (React.createElement(React.Fragment, null, render({
        'aria-describedby': rendersFormGroup ? descriptionId : undefined,
        'aria-invalid': isInvalid,
        disabled: disabled,
        error: error,
        id: id,
        isFormik: true,
        isInvalid: isInvalid,
        isTouched: isTouched,
        isDirty: isDirty,
        name: name,
        onBlur: function (ev) {
            if (ev) {
                fieldProps.field.onBlur(ev);
            }
            var initialValue = getIn(fieldProps.form.initialValues, name);
            var newValue = getIn(fieldProps.form.values, name);
            toggleFieldTouched(initialValue, newValue);
        },
        onChange: function (ev) {
            if (isChangeEvent(ev)) {
                fieldProps.field.onChange(ev);
            }
            else {
                fieldProps.form.setFieldValue(name, ev);
            }
            var initialValue = getIn(fieldProps.form.initialValues, name);
            var newValue = ev && ev.target ? ev.target.value : ev;
            toggleFieldTouched(initialValue, newValue);
        },
        value: fieldProps.field.value
    }, fieldProps.form)));
    if (!rendersFormGroup) {
        return content;
    }
    return (React.createElement(FormGroup, { disabled: disabled, "data-invalid": isInvalid, description: description, error: error, descriptionId: descriptionId, id: id, isInline: isInline, isInvalid: isInvalid, label: label, required: required, lss: groupLss, labelLss: labelLss, showValidationToolTip: showValidationToolTip }, content));
}
function validateAndCheckRequired(validate, isRequired, requiredMessage) {
    if (!isRequired && !validate) {
        return undefined;
    }
    return function (value) {
        // we must check the type in case value is 0
        // and skip this required validation
        if (isRequired && typeof value !== 'number') {
            if (Array.isArray(value) && !value.length) {
                return requiredMessage || 'You must select at least one value';
            }
            if (typeof value === 'undefined' ||
                value === null ||
                (typeof value === 'string' && value === '')) {
                return requiredMessage || 'This field is required';
            }
        }
        return validate ? validate(value) : undefined;
    };
}
/**
 * This will be the main component included when building a form. A field element includes a wrapper, a label, an input control, and an error message, when applicable.
 *
 * Field components must be used inside a Form.
 */
export function Field(props) {
    var disabled = props.disabled, groupLss = props.groupLss, id = props.id, isInline = props.isInline, label = props.label, name = props.name, required = props.required, requiredMessage = props.requiredMessage, validate = props.validate, description = props.description, labelLss = props.labelLss, _a = props.rendersFormGroup, rendersFormGroup = _a === void 0 ? true : _a, showValidationToolTip = props.showValidationToolTip, showDirty = props.showDirty;
    var _b = __read(React.useState(false), 2), isDirty = _b[0], setIsDirty = _b[1];
    var fieldId = id || genId(props.name);
    var render;
    if ('render' in props) {
        render = props.render;
    }
    else {
        // eslint-disable-next-line
        var ref = props.ref, inputProps_1 = __rest(props, ["ref"]);
        render = function (fieldProps) {
            var dirtyLss = isDirty && showDirty && !fieldProps.isInvalid
                ? { bg: 'warning100', borderColor: 'warning400' }
                : {};
            return (React.createElement(TextInput, __assign({}, fieldProps, inputProps_1, { id: fieldId, isFormik: true, lss: __assign(__assign({}, inputProps_1.lss), dirtyLss), key: inputProps_1.key ? inputProps_1.key : undefined, showValidationToolTip: showValidationToolTip })));
        };
    }
    return (React.createElement(FormikField, { name: name, validate: validateAndCheckRequired(validate, required, requiredMessage) }, function (fieldProps) { return (React.createElement(FormControl, { rendersFormGroup: rendersFormGroup, disabled: disabled, fieldProps: fieldProps, description: description, groupLss: groupLss, labelLss: labelLss, id: fieldId, isInline: isInline, label: label, name: name, render: render, required: required, showValidationToolTip: showValidationToolTip, setIsDirty: setIsDirty, isDirty: isDirty })); }));
}
//# sourceMappingURL=Field.js.map